import { Ellipsis, Plus, Sparkles, Store } from 'lucide-react';
import type { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { DeltaBadge } from '@/components/metrics/delta-badge';
import { NAV_ITEM_ICONS, useNavItemLabel } from '@/components/shell/nav-items';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18nContext } from '@/i18n/i18n-react';
import { greetingFor, homeEyebrow } from '@/lib/home-greeting';
import { TAB_BAR_IDS } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import {
  formatDay,
  formatMoney,
  signedDelta,
} from '@/lib/whats-new/formatting';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { StatusBarIcons } from '@/pages/onboarding/mockup/iphone-status-bar';
import type {
  DeltaInput,
  PhoneVM,
} from '@/components/whats-new/editions/estreia/view-models';
import { NO_VALUE } from '@/components/whats-new/editions/estreia/view-models';
import { LineSkeleton } from '@/components/whats-new/slides/line-skeleton';
import { SampleBadge } from '@/components/whats-new/slides/sample-badge';

/** iPhone 17 screen width, in points: the admin is drawn at 1 CSS px = 1 pt. */
export const PHONE_SCREEN_WIDTH = 402;

// The mobile shell's own measures (< 768px), in points: page padding
// (`px-6`), header (`min-h-touch` + `py-1`), tab bar (`h-tab-bar`) and the
// home indicator area below it (safe-area-inset-bottom).
const STATUS_BAR = 54;
const TAB_BAR = 56;
const HOME_INDICATOR_AREA = 34;

// Revenue chart inside the card: the card's inner width at 402pt.
const CHART_WIDTH = 320;
const CHART_HEIGHT = 76;
/** The chart's axis row; reserved while loading (NEWS-26). */
const AXIS = 'mt-1.5 text-[11px]';

/** Same look as the real `Card` below 768px (`rounded-mobile-card`). */
const CARD = 'rounded-mobile-card border bg-card text-card-foreground';

/** The period chips of the Início (`shell.period.chips`), "30 dias" active. */
const CHIP_KEYS = [
  'today',
  'last7Days',
  'last30Days',
  'last3Months',
  'custom',
] as const;

/** Variation of a tile, only when it rose (NEWS-24), as the real tiles draw it. */
function TileDelta({ delta }: { delta: DeltaInput | null }) {
  const text = delta ? signedDelta(delta.current, delta.previous) : null;
  if (text === null) return null;
  return <DeltaBadge delta={{ kind: 'up', text }} />;
}

function ValueSkeleton({ className }: { className?: string }) {
  return (
    <Skeleton
      data-testid="phone-value-loading"
      className={cn('h-7', className)}
    />
  );
}

/** Where the finger is on the chart, from the day's index. */
function chartPoint(index: number, series: readonly number[]) {
  const max = Math.max(1, ...series) * 1.1;
  const step = series.length > 1 ? CHART_WIDTH / (series.length - 1) : 0;
  return {
    x: index * step,
    y: CHART_HEIGHT - 4 - (series[index] / max) * (CHART_HEIGHT - 10),
  };
}

function RevenueChart({
  series,
  markers,
  fingerIndex,
  axis,
}: {
  series: number[];
  markers: number[];
  fingerIndex: number | null;
  axis: string[];
}) {
  const points = series.map((_, i) => chartPoint(i, series));
  const line = points
    .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
  const finger = fingerIndex === null ? null : points[fingerIndex];

  return (
    <div className="relative mt-3">
      <svg
        aria-hidden="true"
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="block overflow-visible"
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={CHART_WIDTH * f}
            x2={CHART_WIDTH * f}
            y1={0}
            y2={CHART_HEIGHT}
            className="stroke-border"
            strokeDasharray="3 3"
          />
        ))}
        <line
          x1={0}
          x2={CHART_WIDTH}
          y1={CHART_HEIGHT}
          y2={CHART_HEIGHT}
          className="stroke-border"
        />
        <path
          d={line}
          fill="none"
          className="stroke-primary"
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {markers.map((index) => (
          <circle
            key={index}
            data-testid="phone-chart-marker"
            cx={points[index]?.x}
            cy={points[index]?.y}
            r={4}
            className="fill-primary stroke-background"
            strokeWidth={2}
          />
        ))}
        {finger && (
          <>
            <line
              x1={finger.x}
              x2={finger.x}
              y1={0}
              y2={CHART_HEIGHT}
              className="stroke-foreground"
              strokeOpacity={0.25}
              strokeDasharray="3 3"
            />
            <circle
              cx={finger.x}
              cy={finger.y}
              r={5.5}
              className="fill-primary stroke-background"
              strokeWidth={2.5}
            />
          </>
        )}
      </svg>
      <span
        data-testid="phone-finger"
        data-active={finger ? true : undefined}
        aria-hidden="true"
        className={cn(
          'absolute -left-[22px] -top-[22px] size-11 rounded-full bg-[rgba(21,25,30,0.14)] shadow-[0_0_0_1px_rgba(21,25,30,0.08)] transition-opacity duration-200',
          finger ? 'opacity-100' : 'opacity-0'
        )}
        style={
          finger
            ? {
                transform: `translate(${finger.x}px, ${Math.min(finger.y + 18, CHART_HEIGHT - 10)}px)`,
              }
            : undefined
        }
      />
      <div className={cn(AXIS, 'flex justify-between text-muted-foreground')}>
        {axis.map((label, i) => (
          <span key={i}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function Row({
  section,
  label,
  children,
}: {
  section: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div data-section={section} className={cn(CARD, 'px-4 py-3')}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-0.5 truncate text-[15px] font-semibold">
        {children}
      </div>
    </div>
  );
}

/**
 * The admin's mobile Início (NEWS-29), drawn in points inside the official
 * `IPhoneMockup` (iPhone 17, 402pt): status bar, header with the store and
 * ✦, date and greeting, the period chips with "30 dias" on, the "Faturamento
 * pelo app" card whose reading follows the finger, the Vendas and Ticket
 * médio tiles, "Hoje até …", "Próxima campanha", the "+" button and the tab
 * bar. The real components respond to the window's width, not the phone's,
 * so this draws the mobile layer's measures and tokens itself.
 */
export function AdminPhoneIllustration({
  vm,
  now,
  fingerIndex,
}: {
  vm: PhoneVM;
  /** The edition's "now" (end of its 30 days): date, greeting and "Hoje até". */
  now: DateTime;
  /** Day under the finger; `null` shows the 30-day total. */
  fingerIndex: number | null;
}) {
  const { LL, locale } = useI18nContext();
  const navLabel = useNavItemLabel();
  const { store, revenue, sales, averageTicket, todaySoFar, nextCampaign } = vm;

  const reading = () => {
    if (revenue.state !== 'ready') return null;
    const { days, currency, total } = revenue.value;
    const day = fingerIndex === null ? undefined : days[fingerIndex];
    if (fingerIndex !== null && day) {
      const isToday = fingerIndex === days.length - 1;
      return {
        value: formatMoney(day.revenue, currency, locale) ?? NO_VALUE,
        detail: LL.home.revenue.pointLabel({
          label: isToday
            ? LL.whatsNew.editions.estreia.home.axisToday()
            : formatDay(day.date, locale),
          count: day.orders,
        }),
      };
    }
    return {
      value: formatMoney(total, currency, locale) ?? NO_VALUE,
      detail: LL.home.revenue.pointLabel({
        label: LL.salesChart.predefinedRanges.last30Days(),
        count: days.reduce((sum, d) => sum + d.orders, 0),
      }),
    };
  };
  const shown = reading();

  const axis =
    revenue.state === 'ready'
      ? (() => {
          const { days } = revenue.value;
          const last = days.length - 1;
          return [0, 1, 2].map((k) => {
            const d = days[Math.round((k * last) / 3)];
            return d ? formatDay(d.date, locale) : '';
          });
        })()
      : [];

  return (
    <IPhoneMockup
      sceneKey="admin-home"
      screenWidth={PHONE_SCREEN_WIDTH}
      chromeBaked
    >
      <div
        data-testid="admin-phone"
        role="img"
        aria-label={LL.whatsNew.editions.estreia.phone.illustrationLabel({
          store: store.name,
        })}
        className="relative h-full w-full overflow-hidden bg-background font-sans text-foreground"
      >
        {/* iOS status bar, in points. */}
        <div
          data-section="status-bar"
          className="absolute inset-x-0 top-0 flex items-center justify-between pl-[46px] pr-[30px]"
          style={{
            height: STATUS_BAR,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <span className="text-[17px] font-semibold">9:41</span>
          <span className="origin-right scale-[1.34]">
            <StatusBarIcons withInset={false} />
          </span>
        </div>

        {/* MobilePageHeader: store (icon + name, truncated) and ✦. */}
        <div
          data-section="header"
          className="absolute inset-x-0 flex items-center gap-2 border-b px-4"
          style={{ top: STATUS_BAR, height: 52 }}
        >
          {store.iconUrl ? (
            <img
              src={store.iconUrl}
              alt=""
              className="size-7 flex-none rounded-lg object-cover"
            />
          ) : (
            <span className="grid size-7 flex-none place-items-center rounded-lg bg-muted">
              <Store aria-hidden="true" className="size-3.5" />
            </span>
          )}
          <span className="min-w-0 truncate text-base font-semibold">
            {store.name}
          </span>
          <span
            data-section="whats-new-button"
            className="relative ml-auto grid size-11 flex-none place-items-center"
          >
            <Sparkles aria-hidden="true" className="size-5" strokeWidth={1.6} />
            <span className="absolute right-2 top-2 size-2 rounded-full bg-[#155DFC] shadow-[0_0_0_2px_#fff]" />
          </span>
        </div>

        <div
          className="absolute inset-x-0 flex flex-col px-6"
          style={{ top: STATUS_BAR + 52 + 24 }}
        >
          <p
            data-section="date"
            className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#155DFC]"
          >
            {homeEyebrow(now, locale, LL)}
          </p>
          <p
            data-section="greeting"
            className="mt-2 truncate text-3xl font-medium leading-tight tracking-[-0.035em]"
          >
            {`${greetingFor(now, LL)}, ${store.name}.`}
          </p>

          <div data-section="chips" className="mt-4 flex gap-2 overflow-hidden">
            {CHIP_KEYS.map((key) => {
              const active = key === 'last30Days';
              return (
                <span
                  key={key}
                  data-active={active || undefined}
                  className={cn(
                    'flex h-11 flex-none items-center rounded-full border px-4 text-sm font-medium',
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background'
                  )}
                >
                  {LL.shell.period.chips[key]()}
                </span>
              );
            })}
          </div>

          <div className="mt-3.5 flex flex-col gap-3">
            <div data-section="revenue" className={cn(CARD, 'p-4')}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{LL.home.revenue.label()}</p>
                {revenue.state === 'ready' && revenue.value.sample && (
                  <SampleBadge className="bg-[#EEF3FF] text-[#0B348C]" />
                )}
              </div>
              {shown ? (
                <>
                  <p
                    data-testid="phone-readout-value"
                    className="mt-2 h-9 text-3xl font-bold leading-9 tabular-nums"
                  >
                    {shown.value}
                  </p>
                  <p
                    data-testid="phone-readout-detail"
                    className="mt-1 h-5 text-sm text-muted-foreground"
                  >
                    {shown.detail}
                  </p>
                </>
              ) : (
                <>
                  <ValueSkeleton className="mt-2 h-9 w-40" />
                  <Skeleton className="mt-1 h-5 w-28" />
                </>
              )}
              {revenue.state === 'ready' ? (
                <RevenueChart
                  series={revenue.value.days.map((d) => d.revenue)}
                  markers={
                    revenue.value.sample
                      ? []
                      : [revenue.value.secondDay, revenue.value.peakDay]
                  }
                  fingerIndex={fingerIndex}
                  axis={[
                    ...axis,
                    LL.whatsNew.editions.estreia.home.axisToday(),
                  ]}
                />
              ) : (
                <div className="mt-3">
                  <Skeleton
                    className="w-full"
                    style={{ height: CHART_HEIGHT }}
                  />
                  {/* The axis row (NEWS-26). */}
                  <div aria-hidden="true" className={cn(AXIS, 'invisible')}>
                    0
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ['sales', LL.home.kpis.orders(), sales],
                  [
                    'average-ticket',
                    LL.home.kpis.averageTicket(),
                    averageTicket,
                  ],
                ] as const
              ).map(([section, label, tile]) => (
                <div
                  key={section}
                  data-section={section}
                  className={cn(CARD, 'flex min-w-0 flex-col gap-1.5 p-3')}
                >
                  <p className="text-xs font-medium text-muted-foreground">
                    {label}
                  </p>
                  {tile.state === 'ready' ? (
                    <>
                      <p className="truncate text-lg font-semibold tabular-nums tracking-[-0.02em]">
                        {tile.value.text}
                      </p>
                      <span className="flex h-5 items-center">
                        <TileDelta delta={tile.value.delta} />
                      </span>
                    </>
                  ) : (
                    <>
                      <ValueSkeleton className="w-20" />
                      {/* The variation's row (NEWS-26). */}
                      <span aria-hidden="true" className="h-5" />
                    </>
                  )}
                </div>
              ))}
            </div>

            <Row
              section="today"
              label={LL.home.today.title({ time: now.toFormat('HH:mm') })}
            >
              {todaySoFar.state === 'ready' ? (
                todaySoFar.value
              ) : (
                <LineSkeleton
                  data-testid="phone-value-loading"
                  className="h-5 w-24"
                />
              )}
            </Row>
            <Row section="next-campaign" label={LL.home.nextCampaign.title()}>
              {nextCampaign.state === 'ready' ? (
                nextCampaign.value(LL)
              ) : (
                <LineSkeleton
                  data-testid="phone-value-loading"
                  className="h-5 w-32"
                />
              )}
            </Row>
          </div>
        </div>

        {/* NewCampaignFab: 16pt above the tab bar, 16pt from the edge. */}
        <span
          data-section="fab"
          className="absolute right-4 grid size-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg"
          style={{ bottom: TAB_BAR + HOME_INDICATOR_AREA + 16 }}
        >
          <Plus aria-hidden="true" className="size-6" />
        </span>

        {/* MobileTabBar, Início active, over the home indicator area. */}
        <div
          data-section="tab-bar"
          className="absolute inset-x-0 bottom-0 border-t bg-background"
          style={{ paddingBottom: HOME_INDICATOR_AREA }}
        >
          <div className="flex items-stretch px-1" style={{ height: TAB_BAR }}>
            {TAB_BAR_IDS.map((id) => {
              const Icon = NAV_ITEM_ICONS[id];
              const active = id === 'home';
              return (
                <span
                  key={id}
                  data-active={active || undefined}
                  className={cn(
                    'flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium leading-none text-muted-foreground',
                    active && 'text-primary'
                  )}
                >
                  <Icon
                    aria-hidden="true"
                    className="size-5"
                    strokeWidth={active ? 2.25 : 2}
                  />
                  <span>{navLabel(id)}</span>
                </span>
              );
            })}
            <span className="flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium leading-none text-muted-foreground">
              <Ellipsis aria-hidden="true" className="size-5" />
              <span>{LL.shell.tabs.more()}</span>
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-2 flex justify-center">
            <span className="h-[5px] w-[139px] rounded-full bg-foreground" />
          </div>
        </div>
      </div>
    </IPhoneMockup>
  );
}
